import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getEmailTemplateCategoryOptions } from '../../../utils/Enums';

const EmailTemplateEditor = ({ open, onClose, template, onSave, mode = 'create', availableTemplates = [] }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [cloneFromTemplate, setCloneFromTemplate] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: '',
    htmlContent: '',
    textContent: ''
  });

  // Actualizar formData cuando cambia el template
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        category: template.category || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || ''
      });
      setCloneFromTemplate('');
    } else {
      // Resetear si no hay template (modo crear)
      setFormData({
        name: '',
        subject: '',
        category: '',
        htmlContent: '',
        textContent: ''
      });
      setCloneFromTemplate('');
    }
  }, [template, open]);

  // Cuando se selecciona una plantilla para clonar
  useEffect(() => {
    if (cloneFromTemplate && mode === 'create') {
      const selectedTemplate = availableTemplates.find(t => t.id === parseInt(cloneFromTemplate));
      if (selectedTemplate) {
        setFormData({
          name: `${selectedTemplate.name} (Copia)`,
          subject: selectedTemplate.subject || '',
          category: selectedTemplate.category || '',
          htmlContent: selectedTemplate.htmlContent || '',
          textContent: selectedTemplate.textContent || ''
        });
      }
    }
  }, [cloneFromTemplate, availableTemplates, mode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  // Variables disponibles para usar en el email
  const availableVariables = [
    { name: '{{clientName}}', description: t('client_name') },
    { name: '{{clientEmail}}', description: t('client_email') },
    { name: '{{companyName}}', description: t('company_name') },
    { name: '{{currentDate}}', description: t('current_date') }
  ];

  const insertVariable = (variable) => {
    const currentContent = formData.htmlContent;
    const newContent = currentContent + variable;
    handleChange('htmlContent', newContent);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('document')}
        {mode === 'create' ? t('create_template') : t('edit_template')}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {mode === 'create' && availableTemplates.length > 0 && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('clone_from_template')}</InputLabel>
                <Select
                  value={cloneFromTemplate}
                  label={t('clone_from_template')}
                  onChange={(e) => setCloneFromTemplate(e.target.value)}
                >
                  <MenuItem value="">{t('start_from_scratch')}</MenuItem>
                  {availableTemplates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} {t.isSystem && `(${t('system_template')})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('clone_template_help')}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('name')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('category')}</InputLabel>
              <Select
                value={formData.category}
                label={t('category')}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('select_category') || 'Seleccionar categoría'}</em>
                </MenuItem>
                {getEmailTemplateCategoryOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('subject')}
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              required
              placeholder={t('email_subject_placeholder')}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t('available_variables')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {availableVariables.map((variable) => (
                <Chip
                  key={variable.name}
                  label={`${variable.name} - ${variable.description}`}
                  onClick={() => insertVariable(variable.name)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label={t('html_editor')} />
                <Tab label={t('preview')} />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label={t('html_content')}
                  value={formData.htmlContent}
                  onChange={(e) => handleChange('htmlContent', e.target.value)}
                  placeholder={t('html_content_placeholder')}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('html_content_help')}
                </Typography>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, minHeight: 400, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    {formData.subject || t('email_preview')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    dangerouslySetInnerHTML={{ __html: formData.htmlContent || t('no_content') }}
                    sx={{
                      '& img': { maxWidth: '100%' },
                      '& a': { color: 'primary.main' }
                    }}
                  />
                </Paper>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.name || !formData.subject || !formData.htmlContent}
          startIcon={getIcon('checkmarkDone')}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateEditor;
